import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ImagePlus, Upload, X } from "lucide-react";

import Navbar from "../../components/site/Navbar";
import Footer from "../../components/site/Footer";
import { useGarment } from "../../hooks/useGarment";

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ACCEPTED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
];

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Unable to read the uploaded image."));
    reader.readAsDataURL(file);
  });
}

export default function UploadDesignPage() {
  const { categoryId, sectionId, garmentId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const { garment, garmentLoading } = useGarment(garmentId);

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  const selectFile = (file) => {
    setError("");

    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError("Please upload a JPG, PNG or WebP image.");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError("The image must be smaller than 10 MB.");
      return;
    }

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const removeFile = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setSelectedFile(null);
    setPreviewUrl("");
    setError("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const continueWithDesign = async () => {
    if (!selectedFile) {
      setError("Please select a design image first.");
      return;
    }

    try {
      const imageDataUrl = await fileToDataUrl(selectedFile);
      const draft = {
        source: "uploaded_design",
        garmentId,
        garmentName: garment?.name || "",
        fileName: selectedFile.name,
        notes: notes.trim(),
        imageUrl: imageDataUrl,
        previewUrl: imageDataUrl,
        imageDataUrl,
      };

      sessionStorage.setItem(
        "stichb_design_draft",
        JSON.stringify(draft)
      );

      navigate(
        `/category/${categoryId}/section/${sectionId}/item/${garmentId}/design-confirmation`
      );
    } catch (imageError) {
      setError(imageError?.message || "Unable to read the uploaded image.");
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF8F2]">
      <Navbar onBook={() => navigate("/#services")} />

      <main className="pt-28 pb-24">
        <section className="max-w-[1000px] mx-auto px-6 md:px-12">
          <button
            type="button"
            onClick={() =>
              navigate(
                `/category/${categoryId}/section/${sectionId}/item/${garmentId}/designs`
              )
            }
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#F46524]"
          >
            <ArrowLeft size={16} />
            Back to design options
          </button>

          <div className="mt-8">
            <div className="font-label text-[#F46524] mb-4">
              Upload Your Design
            </div>

            <h1 className="font-display text-4xl md:text-6xl tracking-tight text-[#2B2B2B]">
              Share your reference design
            </h1>

            <p className="text-[#5b5a56] mt-5 text-lg leading-relaxed">
              Upload a clear reference image for{" "}
              <strong>
                {garmentLoading ? "your garment" : garment?.name || "your garment"}
              </strong>.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="card-soft p-6">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(event) =>
                  selectFile(event.target.files?.[0])
                }
              />

              {!previewUrl ? (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    selectFile(event.dataTransfer.files?.[0]);
                  }}
                  className="w-full min-h-[360px] rounded-2xl border-2 border-dashed border-[#F4C8A7] bg-[#FFF8F2] flex flex-col items-center justify-center p-8 text-center hover:border-[#F46524] transition-colors"
                >
                  <span className="w-16 h-16 rounded-full bg-[#FFEDDD] text-[#F46524] flex items-center justify-center">
                    <ImagePlus size={28} />
                  </span>

                  <span className="font-display text-2xl mt-5">
                    Choose a design image
                  </span>

                  <span className="text-sm text-[#5b5a56] mt-2">
                    Drag and drop or click to browse
                  </span>

                  <span className="text-xs text-[#8a8781] mt-3">
                    JPG, PNG or WebP · Maximum 10 MB
                  </span>
                </button>
              ) : (
                <div className="relative rounded-2xl overflow-hidden bg-[#FFF3E8]">
                  <img
                    src={previewUrl}
                    alt="Uploaded design preview"
                    className="w-full max-h-[520px] object-contain"
                  />

                  <button
                    type="button"
                    onClick={removeFile}
                    className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white shadow flex items-center justify-center hover:bg-[#F46524] hover:text-white"
                    aria-label="Remove selected image"
                  >
                    <X size={17} />
                  </button>
                </div>
              )}

              {error ? (
                <p className="text-sm text-red-600 mt-4">
                  {error}
                </p>
              ) : null}
            </div>

            <div className="card-soft p-7">
              <h2 className="font-display text-2xl">
                Design instructions
              </h2>

              <p className="text-sm text-[#5b5a56] mt-2">
                Mention changes such as sleeves, neckline, length,
                embroidery, colour or fitting preference.
              </p>

              <textarea
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                rows={10}
                maxLength={1200}
                placeholder="Example: Keep the same neckline, add elbow-length sleeves and use gold embroidery around the border."
                className="w-full mt-6 rounded-2xl border border-[#E9D7C8] bg-white p-4 outline-none focus:border-[#F46524] resize-none"
              />

              <div className="text-xs text-[#8a8781] text-right mt-2">
                {notes.length}/1200
              </div>

              <button
                type="button"
                onClick={continueWithDesign}
                className="btn-primary w-full justify-center mt-7"
              >
                <Upload size={18} />
                Continue with this design
              </button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
